import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge } from "@/components/common/kit";
import DataTable from "@/components/common/DataTable";
import { SalesmanForm, ResetPasswordDialog, OpeningStockDialog } from "@/components/admin/SalesmanDialogs";
import { OVERVIEW_COLS } from "./Dashboard";

export default function Salesmen() {
  const { bizDate } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(bizDate);
  const [form, setForm] = useState(null);
  const [reset, setReset] = useState(null);
  const [opening, setOpening] = useState(null);
  const { data: list, loading, reload } = useFetch("/salesmen");
  const { data: ov } = useFetch("/dashboard/overview", { date });

  const cols = [
    { key: "code", label: "ID" },
    { key: "full_name", label: "Name" },
    { key: "mobile", label: "Mobile" },
    { key: "employee_code", label: "Emp. Code" },
    { key: "username", label: "Login ID" },
    { key: "joining_date", label: "Joined", type: "date" },
    { key: "stock_packs", label: "Stock (pks)", type: "int" },
    { key: "outstanding", label: "Receivable", type: "money" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} testid={`sm-status-${r.code}`} /> },
    {
      key: "actions", label: "", sortable: false, noExport: true,
      render: (r) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {!r.has_ledger && r.status === "ACTIVE" && <Button size="sm" variant="outline" onClick={() => setOpening(r)} data-testid={`sm-opening-${r.code}`}>Opening stock</Button>}
          <Button size="sm" variant="ghost" onClick={() => setForm(r)} data-testid={`sm-edit-${r.code}`}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => setReset(r)} data-testid={`sm-reset-${r.code}`}>Reset PW</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Salesmen" subtitle="Accounts, daily overview and drill-down"
        actions={<Button onClick={() => setForm({})} data-testid="new-salesman-button"><Plus className="mr-1.5 h-4 w-4" /> New Salesman</Button>} />
      <Tabs defaultValue="accounts">
        <TabsList><TabsTrigger value="accounts" data-testid="tab-accounts">Accounts</TabsTrigger><TabsTrigger value="overview" data-testid="tab-overview">Daily Overview</TabsTrigger></TabsList>
        <TabsContent value="accounts">
          <Section>
            <DataTable columns={cols} rows={list || []} loading={loading} onRowClick={(r) => nav(`/admin/salesmen/${r.id}`)} title="Salesmen" exportable testid="salesmen-table" />
          </Section>
        </TabsContent>
        <TabsContent value="overview">
          <Section actions={<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" data-testid="overview-date" />} title={`Overview`}>
            <DataTable columns={OVERVIEW_COLS} rows={ov?.rows || []} onRowClick={(r) => nav(`/admin/day/${r.salesman_id}/${date}`)}
              totals={["opening", "issued", "sold", "closing", "sales_value", "cash", "gpay", "receivable", "expenses"]} title={`Salesman Overview ${date}`} exportable testid="overview-table" />
          </Section>
        </TabsContent>
      </Tabs>
      <SalesmanForm open={!!form} salesman={form?.id ? form : null} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />
      <ResetPasswordDialog salesman={reset} onClose={() => setReset(null)} />
      <OpeningStockDialog salesman={opening} onClose={() => setOpening(null)} onSaved={() => { setOpening(null); reload(); }} />
    </div>
  );
}
